/**
 * ============================================================================
 * SubjectEntityPicker — Phase 6c frontend (CCIE).
 * Owner: Claude 3 (Frontend & UX).
 * Purpose: Step 2 of the CC proposal wizard. The user has picked a change
 *          type; this step asks them to identify the *subject entity* — the
 *          document / equipment / supplier / regulation / system whose
 *          modification triggers the impact analysis.
 *
 * Data: the searchable picker hits a generic entity-search endpoint scoped to
 *  the entity kind implied by the selected change type. Each change type maps
 *  to one (sometimes two) entity-kinds; the mapping is computed here so the
 *  parent component stays simple.
 *
 * A11Y:
 *   - role="listbox" with selected/aria-activedescendant patterns; for keyboard
 *     users, Up/Down move the highlight and Enter selects.
 *   - The free-text input has aria-controls pointing at the results list.
 * ============================================================================ */

import { useEffect, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '../../api/client';
import styles from './ChangeRequestProposal.module.css';
import type { ChangeType } from './useCreateProposalMutation';

export interface SubjectEntity {
  entity_id: string;
  entity_kind: string;
  display_name: string;
  subtitle?: string;
}

export interface SubjectEntitySearchResponse {
  items: SubjectEntity[];
}

/**
 * Per-type entity-kind hint sent to the search endpoint so the backend can
 * narrow the index. The list is intentionally small — the backend may also
 * accept the change_type itself, but the explicit kind makes the URL
 * grep-friendly in audit logs.
 */
const ENTITY_KIND_BY_TYPE: Record<ChangeType, string[]> = {
  DOCUMENT_CONTENT: ['DOCUMENT'],
  DOCUMENT_OWNERSHIP: ['DOCUMENT'],
  EQUIPMENT_RELOCATION: ['EQUIPMENT'],
  EQUIPMENT_COMPONENT: ['EQUIPMENT'],
  SUPPLIER_CHANGE: ['SUPPLIER'],
  SUPPLIER_MATERIAL: ['SUPPLIER', 'MATERIAL'],
  VALIDATION_STATUS: ['EQUIPMENT', 'SYSTEM'],
  REGULATORY: ['REGULATORY_ANCHOR'],
  SYSTEM_CONFIG: ['SYSTEM'],
};

export interface SubjectEntityPickerProps {
  changeType: ChangeType;
  value: SubjectEntity | null;
  onChange: (entity: SubjectEntity) => void;
}

export function SubjectEntityPicker({
  changeType,
  value,
  onChange,
}: SubjectEntityPickerProps) {
  const { t } = useTranslation();
  const inputId = useId();
  const listId = useId();

  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);

  // Debounce the query so we don't hammer the API as the user types. 200 ms
  // is the same debounce used by the document-search popover elsewhere.
  const [debouncedQuery, setDebouncedQuery] = useState(query);
  useEffect(() => {
    const handle = window.setTimeout(() => setDebouncedQuery(query), 200);
    return () => window.clearTimeout(handle);
  }, [query]);

  const kindHints = ENTITY_KIND_BY_TYPE[changeType] ?? [];

  const searchQ = useQuery<SubjectEntitySearchResponse>({
    queryKey: ['changes', 'subject-search', changeType, debouncedQuery],
    enabled: debouncedQuery.trim().length >= 1,
    queryFn: ({ signal }) =>
      apiFetch<SubjectEntitySearchResponse>('/api/v1/changes/subject-search', {
        query: {
          q: debouncedQuery.trim(),
          kinds: kindHints.join(','),
        },
        signal,
      }),
  });

  const items: SubjectEntity[] = useMemo(() => searchQ.data?.items ?? [], [
    searchQ.data,
  ]);

  // Reset the highlight when the result-set changes underneath us.
  useEffect(() => {
    setHighlight(0);
  }, [items.length]);

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (items.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(items.length - 1, h + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(0, h - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const target = items[highlight];
      if (target) onChange(target);
    }
  }

  return (
    <div className={styles.subjectPicker}>
      <label htmlFor={inputId} className={styles.fieldLabel}>
        {t('changeControl.proposal.subjectPicker.label', {
          defaultValue: 'Subject entity',
        })}
        <span className={styles.required} aria-hidden="true">
          *
        </span>
      </label>
      <p className={styles.fieldHelp}>
        {t('changeControl.proposal.subjectPicker.help', {
          defaultValue:
            'Search the {{kinds}} that this change directly modifies.',
          kinds: kindHints.join(' / '),
        })}
      </p>
      <input
        id={inputId}
        type="search"
        role="combobox"
        autoComplete="off"
        aria-expanded={items.length > 0}
        aria-controls={listId}
        aria-activedescendant={items[highlight] ? `${listId}-${highlight}` : undefined}
        className={styles.searchInput}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={onKeyDown}
        placeholder={t('changeControl.proposal.subjectPicker.placeholder', {
          defaultValue: 'Search by id, number or title…',
        })}
      />

      {value && (
        <div
          className={styles.pickedEntity}
          role="status"
          aria-live="polite"
          aria-label={t('changeControl.proposal.subjectPicker.pickedAria', {
            defaultValue: 'Picked subject: {{name}}',
            name: value.display_name,
          })}
        >
          <span className={styles.pickedKind} aria-hidden="true">
            {value.entity_kind}
          </span>
          <strong>{value.display_name}</strong>
          {value.subtitle && (
            <span className={styles.pickedSubtitle}>{value.subtitle}</span>
          )}
        </div>
      )}

      {searchQ.isLoading && debouncedQuery.trim().length >= 1 && (
        <p className={styles.fieldHelp} role="status">
          {t('changeControl.proposal.subjectPicker.searching', {
            defaultValue: 'Searching…',
          })}
        </p>
      )}

      {searchQ.isError && (
        <div role="alert" className={styles.inlineError}>
          {t('changeControl.proposal.subjectPicker.searchError', {
            defaultValue: 'Search failed. Try again.',
          })}
        </div>
      )}

      {items.length > 0 && (
        <ul
          id={listId}
          role="listbox"
          className={styles.searchResults}
          aria-label={t('changeControl.proposal.subjectPicker.resultsAria', {
            defaultValue: 'Search results',
          })}
        >
          {items.map((item, i) => {
            const optionId = `${listId}-${i}`;
            const selected = value?.entity_id === item.entity_id;
            const highlighted = i === highlight;
            return (
              <li
                id={optionId}
                key={item.entity_id}
                role="option"
                aria-selected={selected}
                className={`${styles.searchResult} ${
                  highlighted ? styles.searchResultHighlight : ''
                } ${selected ? styles.searchResultSelected : ''}`}
                onMouseEnter={() => setHighlight(i)}
                onClick={() => onChange(item)}
              >
                <span className={styles.pickedKind} aria-hidden="true">
                  {item.entity_kind}
                </span>
                <span className={styles.resultName}>{item.display_name}</span>
                {item.subtitle && (
                  <span className={styles.pickedSubtitle}>{item.subtitle}</span>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
